import {
  EVIDENCE_KINDS,
  PROJECTION_OWNERS,
  PROJECTION_SCHEMA_VERSION,
  WORKSPACE_TOOLS,
  WORKSPACE_TOOL_OWNERS,
  assertProjectionEnvelope,
  assertWorkspaceToolProjection,
} from "./projectionContract";
import {
  LEAD_FIXTURE,
  DISCOVERY_FIXTURE,
  QUOTE_FIXTURE,
  CALENDAR_FIXTURE,
  NOTES_FIXTURE,
} from "./workspaceFixtures";

export const FAKE_RUNTIME_SOURCE = "missionassist-boundary-a-static-fixture";
export const FAKE_ACCOUNT_ID = "fake-account-001";
export const FAKE_ACCOUNT_EPOCH = 1;
export const FAKE_LOGICAL_NOW = 1000;

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

function projection(owner, data, overrides = {}) {
  return {
    schemaVersion: PROJECTION_SCHEMA_VERSION,
    projectionId: `fake-${owner}-001`,
    owner,
    accountId: FAKE_ACCOUNT_ID,
    accountEpoch: FAKE_ACCOUNT_EPOCH,
    revision: 1,
    observedAt: FAKE_LOGICAL_NOW,
    freshUntil: FAKE_LOGICAL_NOW + 100,
    provenance: { kind: "fake", source: FAKE_RUNTIME_SOURCE },
    authorizationDecisionRef:
      owner === PROJECTION_OWNERS.AUTHORIZATION ? "fake-auth-decision-001" : null,
    evidence: { kind: EVIDENCE_KINDS.KNOWN },
    data,
    ...overrides,
  };
}

// script/run: WORKSPACE-AUDIT-007 §13 item 6 correction. Previously
// "missing"/"absent" (no assignment, no Run) — unreviewed placeholder
// values with no test dependency (confirmed before this change), not a
// deliberate scenario. The baseline now legitimately represents "a Script
// is assigned and a Run is attached" — the state the default demo has
// always visually shown via SCRIPT_BLOCKS_FIXTURE — via
// workspaceViewModel.js's resolveScriptRunAdmission, which interprets
// these same two coarse fields into the full candidate/pending/attached/
// unknown model. Neither field's coarse PROJECTION_FIELD_ALLOWLISTS entry
// (still exactly ["assignmentState"]/["runState"]) is widened by this.
const BASELINE = Object.freeze({
  account: projection(PROJECTION_OWNERS.ACCOUNT, { status: "resolved" }),
  authorization: projection(PROJECTION_OWNERS.AUTHORIZATION, { visibility: "allowed" }),
  telephony: projection(PROJECTION_OWNERS.TELEPHONY, {
    sessionState: "ended",
    presenceState: "unavailable",
    connectionState: "connected",
    callState: "absent",
  }),
  script: projection(PROJECTION_OWNERS.SCRIPT, { assignmentState: "resolved" }),
  run: projection(PROJECTION_OWNERS.RUN, { runState: "attached" }),
  crm: projection(PROJECTION_OWNERS.CRM, { resolutionState: "missing" }),
});

function scenario(overrides = {}) {
  return deepFreeze({ ...BASELINE, ...overrides });
}

export const FAKE_SCENARIOS = deepFreeze({
  baseline: scenario(),
  success: scenario(),
  ready: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, {
      sessionState: "unavailable",
      presenceState: "unavailable",
      connectionState: "connected",
      callState: "absent",
    }),
  }),
  active: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, {
      sessionState: "available",
      presenceState: "available",
      connectionState: "connected",
      callState: "absent",
    }),
  }),
  // ENG-009: deterministic fake Reconnecting evidence. workspaceStateStore.js
  // already maps connectionState: "reconnecting" to productState
  // RECONNECTING (ENG-004 origin), but no scenario previously exercised it
  // end-to-end — this closes that gap for ENG-009 §7's required Reconnecting
  // presentation tests.
  reconnecting: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, {
      sessionState: "available",
      presenceState: "available",
      connectionState: "reconnecting",
      callState: "absent",
    }),
  }),
  missing: scenario({ crm: undefined }),
  stale: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, BASELINE.telephony.data, {
      observedAt: FAKE_LOGICAL_NOW - 100,
      freshUntil: FAKE_LOGICAL_NOW - 1,
    }),
  }),
  restricted: scenario({
    crm: projection(PROJECTION_OWNERS.CRM, { resolutionState: "restricted" }, {
      evidence: { kind: EVIDENCE_KINDS.RESTRICTED },
    }),
  }),
  conflicting: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, BASELINE.telephony.data, {
      evidence: { kind: EVIDENCE_KINDS.CONFLICTING, reason: "fake-session-conflict" },
    }),
  }),
  crossAccount: scenario({
    crm: projection(PROJECTION_OWNERS.CRM, { resolutionState: "known" }, {
      accountId: "fake-other-account-002",
    }),
  }),
  unknown: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, BASELINE.telephony.data, {
      evidence: { kind: EVIDENCE_KINDS.UNKNOWN, reason: "fake-owner-outcome-indeterminate" },
    }),
  }),
  definitiveFailure: scenario({
    telephony: projection(
      PROJECTION_OWNERS.TELEPHONY,
      {
        sessionState: "ended",
        presenceState: "unavailable",
        connectionState: "disconnected",
        callState: "absent",
      },
      { evidence: { kind: EVIDENCE_KINDS.FAILURE, reason: "provider-disconnected" } }
    ),
  }),
  reconciliationRequired: scenario({
    telephony: projection(PROJECTION_OWNERS.TELEPHONY, BASELINE.telephony.data, {
      evidence: {
        kind: EVIDENCE_KINDS.RECONCILIATION_REQUIRED,
        reason: "fake-owner-reconciliation-required",
      },
    }),
  }),
});

// --- WORKSPACE-AUDIT-007 §13 item 4: fake tool-projection envelopes ---
//
// Deterministic per-tool envelopes for the five reviewed WORKSPACE_TOOLS
// (Script/Run is out of scope here — see ScriptPanel/WorkspaceMainCard's
// separate, narrower wiring). Data comes directly from the existing
// reviewed fixtures, unchanged, so the default/happy-path shape presented
// through the composition chain is identical to the pre-item-4 direct
// fixture render.
function toolProjection(tool, data, overrides = {}) {
  return {
    schemaVersion: PROJECTION_SCHEMA_VERSION,
    projectionId: `fake-tool-${tool}-001`,
    owner: WORKSPACE_TOOL_OWNERS[tool],
    accountId: FAKE_ACCOUNT_ID,
    accountEpoch: FAKE_ACCOUNT_EPOCH,
    revision: 1,
    observedAt: FAKE_LOGICAL_NOW,
    freshUntil: FAKE_LOGICAL_NOW + 100,
    provenance: { kind: "fake", source: FAKE_RUNTIME_SOURCE },
    evidence: { kind: EVIDENCE_KINDS.KNOWN },
    data,
    ...overrides,
  };
}

const TOOL_BASELINE = Object.freeze({
  [WORKSPACE_TOOLS.LEAD]: toolProjection(WORKSPACE_TOOLS.LEAD, {
    customerInformation: LEAD_FIXTURE.customerInformation,
    address: LEAD_FIXTURE.address,
    leadInformation: LEAD_FIXTURE.leadInformation,
  }),
  [WORKSPACE_TOOLS.DISCOVERY]: toolProjection(WORKSPACE_TOOLS.DISCOVERY, {
    subjects: DISCOVERY_FIXTURE.subjects,
    fields: DISCOVERY_FIXTURE.fields,
    values: DISCOVERY_FIXTURE.values,
  }),
  [WORKSPACE_TOOLS.QUOTE]: toolProjection(WORKSPACE_TOOLS.QUOTE, {
    subjects: QUOTE_FIXTURE.subjects,
    options: QUOTE_FIXTURE.options,
    fields: QUOTE_FIXTURE.fields,
    values: QUOTE_FIXTURE.values,
  }),
  [WORKSPACE_TOOLS.CALENDAR]: toolProjection(WORKSPACE_TOOLS.CALENDAR, {
    selectedDate: CALENDAR_FIXTURE.selectedDate,
    appointments: CALENDAR_FIXTURE.appointments,
  }),
  [WORKSPACE_TOOLS.NOTES]: toolProjection(WORKSPACE_TOOLS.NOTES, {
    currentUser: NOTES_FIXTURE.currentUser,
    notes: NOTES_FIXTURE.notes,
  }),
});

function toolScenario(overrides = {}) {
  return deepFreeze({ ...TOOL_BASELINE, ...overrides });
}

// Reuses FAKE_SCENARIOS' own scenario-name vocabulary so one scenarioName
// continues to select both the coarse per-domain projections and these
// tool projections together. Only "missing" currently overrides a tool
// projection (Lead, the CRM-owned tool — paralleling FAKE_SCENARIOS'
// existing `crm: undefined` override for the same scenario name), which
// is enough to prove the wiring end-to-end without building the full
// per-tool exceptional-state matrix; that per-tool matrix is
// WORKSPACE-AUDIT-007 §13 item 7's job, not item 4's.
export const FAKE_TOOL_PROJECTIONS = deepFreeze(
  Object.keys(FAKE_SCENARIOS).reduce((accumulator, name) => {
    accumulator[name] =
      name === "missing" ? toolScenario({ [WORKSPACE_TOOLS.LEAD]: undefined }) : toolScenario();
    return accumulator;
  }, {})
);

function clone(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

export function createAgentWorkspaceFakeRuntime({
  scenarioName = "baseline",
  logicalNow = FAKE_LOGICAL_NOW,
  environment = process.env.NODE_ENV,
} = {}) {
  if (environment === "production") {
    throw new Error("Boundary A fake runtime is unavailable in production");
  }
  if (!Object.prototype.hasOwnProperty.call(FAKE_SCENARIOS, scenarioName)) {
    throw new Error(`Unknown fake scenario: ${scenarioName}`);
  }

  let selectedScenario = scenarioName;
  let currentLogicalTime = logicalNow;

  function read() {
    const projections = clone(FAKE_SCENARIOS[selectedScenario]);
    Object.values(projections).forEach((item) => {
      if (item !== undefined) {
        assertProjectionEnvelope(item);
      }
    });
    const toolProjections = clone(FAKE_TOOL_PROJECTIONS[selectedScenario]);
    Object.entries(toolProjections).forEach(([tool, item]) => {
      if (item !== undefined) {
        assertWorkspaceToolProjection(tool, item);
      }
    });
    return deepFreeze({
      scenarioName: selectedScenario,
      logicalNow: currentLogicalTime,
      provenance: "FAKE / NON-PRODUCTION",
      projections,
      toolProjections,
    });
  }

  return Object.freeze({
    read,
    reset() {
      selectedScenario = scenarioName;
      currentLogicalTime = logicalNow;
      return read();
    },
  });
}
